import { ref } from "vue";
import ProjectService from "../services/projectService.js";

const projectService = new ProjectService();

// Module-level singleton, keyed to whichever project is currently open —
// shared by Project.vue and useDocumentUpload's post-upload refresh, so a
// document uploaded into the open project shows up without a manual reload.
const docs = ref([]);
const isLoading = ref(false);
const isLoadingMore = ref(false);
const hasMore = ref(true);
let cursor = null;
let currentProjectId = null;
let lastSortBy = "LAST_MODIFIED";
let lastSortDirection = "DESC";
let refreshPromise = null;

export function useProjectDocs() {
  async function loadFirstPage(projectId, sortBy = "LAST_MODIFIED", sortDirection = "DESC") {
    currentProjectId = projectId;
    lastSortBy = sortBy;
    lastSortDirection = sortDirection;
    docs.value = [];
    cursor = null;
    hasMore.value = true;
    isLoading.value = true;
    try {
      await loadMore(projectId);
    } finally {
      isLoading.value = false;
    }
  }

  async function loadMore(projectId) {
    const pid = projectId ?? currentProjectId;
    if (!pid || !hasMore.value || isLoadingMore.value) return;
    isLoadingMore.value = true;
    try {
      const res = await projectService.getDocuments({
        projectId: pid, pageSize: 30, endCursor: cursor,
        sortBy: lastSortBy, sortDirection: lastSortDirection,
      });
      docs.value.push(...(res.documents ?? []));
      cursor = res.endCursor;
      hasMore.value = res.hasNextPage;
    } finally {
      isLoadingMore.value = false;
    }
  }

  // Silently re-fetch enough pages to at least cover what was already loaded
  // (so a newly uploaded doc shows up), swapping the list in one shot rather
  // than clearing-then-refilling, to avoid a visible empty-state flash.
  //
  // Multiple files finishing upload around the same time (e.g. a cloud
  // storage batch) each trigger their own refresh() call — without
  // coalescing, those race as independent fetches, and whichever response
  // resolves LAST wins even if it started first and carries staler data
  // (e.g. a thumbnail not backfilled yet), so a fresher response with the
  // real preview can get overwritten. Concurrent calls just await the one
  // in-flight fetch instead of starting redundant ones.
  function refresh(projectId) {
    if (projectId !== currentProjectId) return Promise.resolve();
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
      const targetCount = Math.max(docs.value.length, 30);
      let items = [];
      let pageCursor = null;
      let more = true;
      while (items.length < targetCount && more) {
        const res = await projectService.getDocuments({
          projectId, pageSize: 30, endCursor: pageCursor,
          sortBy: lastSortBy, sortDirection: lastSortDirection,
        });
        items = items.concat(res.documents ?? []);
        pageCursor = res.endCursor;
        more = res.hasNextPage;
      }
      docs.value = items;
      cursor = pageCursor;
      hasMore.value = more;
    })().finally(() => { refreshPromise = null; });
    return refreshPromise;
  }

  function remove(docId) {
    docs.value = docs.value.filter((d) => d.id !== docId);
  }

  return { docs, isLoading, isLoadingMore, hasMore, loadFirstPage, loadMore, refresh, remove };
}
